import { Button } from '@kk/ui/components/Button';
import { InlineNotification } from '@kk/ui/components/Notifications/InlineNotification';
import { useToast } from '@kk/ui/components/Notifications/Toast';
import { TopLineBanner } from '@kk/ui/components/Notifications/TopLineBanner';
import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { getOperationId } from '#/api/errors';
import { appInsightsPlugin } from '#/app-insights';
import state from '#/state';

class AppInsightsErrorBoundary extends React.Component<{
  onError: (error: Error | undefined) => React.ReactNode;
  children: React.ReactNode;
  appInsights: ReactPlugin;
}> {
  state: { hasError: boolean; error: Error | undefined } = {
    hasError: false,
    error: undefined,
  };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ hasError: true, error: error });
    if (import.meta.env.MODE === 'production') {
      this.props.appInsights.trackException({
        error: error,
        exception: error,
        severityLevel: SeverityLevel.Error,
        properties: { ...info },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      const { onError } = this.props;
      return typeof onError === 'function'
        ? onError(this.state.error)
        : React.createElement(onError);
    }

    return this.props.children;
  }
}

function ComponentErrorMessageDisplay({ error }: { error: Error | undefined }) {
  const { showToast } = useToast();
  const { devtoolsEnabled } = useSnapshot(state);
  const { t } = useTranslation('common');
  const [isStackExpanded, setIsStackExpanded] = React.useState(false);

  const handleCopyError = React.useCallback(async () => {
    if (!error) return;

    const errorDetails = [
      `Error: ${error.message}`,
      ...(error.stack ? [`\nStack trace:\n${error.stack}`] : []),
    ].join('\n');

    try {
      await navigator.clipboard.writeText(errorDetails);
      showToast({
        type: 'info',
        headline: t('error.copy.success.headline', 'Copied to clipboard'),
        message: t(
          'error.copy.success.message',
          'Error details have been copied',
        ),
        size: 'short',
      });
    } catch (_) {
      showToast({
        type: 'danger',
        headline: t('error.copy.failure.headline', 'Copy failed'),
        message: t(
          'error.copy.failure.message',
          'Unable to copy error details',
        ),
        size: 'short',
      });
    }
  }, [error, showToast, t]);

  if (!error) return null;

  const showStack = devtoolsEnabled || import.meta.env.MODE === 'development';

  const stackLines = error.stack?.split('\n') || [];
  const maxVisibleLines = 5;
  const shouldTruncate = stackLines.length > maxVisibleLines;

  return (
    <div className="w-full space-y-2 mt-2">
      <pre className="typ-code p-2 bg-white/50 rounded-md text-xs leading-relaxed whitespace-pre-wrap break-all max-w-full">
        <div className="font-semibold text-red-110 break-all">
          {error.name || 'Error'}: {error.message}
        </div>
        {showStack && error.stack && (
          <div className="mt-1.5 pt-1.5 border-t border-neutral-30">
            <div className="text-2xs text-neutral-80 mb-0.5 select-none font-bold">
              Stack trace:
            </div>
            <div
              className={`text-neutral-90 font-mono text-2xs leading-tight break-all my-2 ${
                !isStackExpanded ? 'line-clamp-5' : ''
              }`}
            >
              {error.stack}
            </div>
            {shouldTruncate && (
              <button
                type="button"
                onClick={() => setIsStackExpanded(!isStackExpanded)}
                className="mt-1 w-full text-left font-bold select-none text-2xs text-blue-110 hover:text-blue-120 underline cursor-pointer"
              >
                {isStackExpanded
                  ? t('error.stack.collapse')
                  : t('error.stack.expand')}
              </button>
            )}
          </div>
        )}
      </pre>
      <div className="flex justify-end">
        <Button
          theme="danger"
          compact
          icon="copy"
          className="select-none"
          label={t('error.copy.button')}
          onClick={handleCopyError}
          aria-label={t('error.copy.button')}
        />
      </div>
    </div>
  );
}

export function ErrorBoundary({
  children,
  inline = true,
  className,
  silent,
}: {
  children?: React.ReactNode;
  inline?: boolean;
  className?: string;
  silent?: boolean;
}) {
  const { devtoolsEnabled } = useSnapshot(state);
  const { reset } = useQueryErrorResetBoundary();
  const { t } = useTranslation('common');

  return (
    <AppInsightsErrorBoundary
      onError={(error) => {
        const operationId = error ? getOperationId(error) : undefined;
        const operationIdString = operationId
          ? ` (OperationId: ${operationId})`
          : '';
        if (devtoolsEnabled || import.meta.env.MODE === 'test')
          console.error(error?.message);
        if (silent) return null;
        if (inline) {
          return (
            <InlineNotification
              data-testid="inline-error"
              type="danger"
              size="medium"
              headline={t('error.component.title')}
              message={<ComponentErrorMessageDisplay error={error} />}
              className={className}
              onDismiss={() => {
                reset();
              }}
            />
          );
        }
        const toplineBanner = (
          <TopLineBanner
            data-testid="error-banner"
            type="danger"
            message={t('error.api.title')}
            label={
              (devtoolsEnabled ? error?.message : t('error.api.message')) +
              operationIdString
            }
            className={className}
            onDismiss={() => {
              reset();
              window.location.reload();
            }}
          />
        );
        const portalTarget = document.getElementById('error-portal');
        if (portalTarget) {
          return createPortal(toplineBanner, portalTarget);
        }
        return toplineBanner;
      }}
      appInsights={appInsightsPlugin}
    >
      {children}
    </AppInsightsErrorBoundary>
  );
}
